import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { ShieldCheck, LockKey, UploadSimple, DownloadSimple, Plus, Trash, UserMinus, FloppyDisk, ArrowsClockwise } from "@phosphor-icons/react";

const TODAY = () => new Date().toISOString().slice(0, 10);

export default function CipEvidenceRequestTool() {
  const [cfg, setCfg] = useState(null);
  const [activeTab, setActiveTab] = useState("bes_assets");
  const [rowsByTab, setRowsByTab] = useState({});
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(() => {
    api.get("/ert/config").then((r) => {
      setCfg(r.data);
      setActiveTab(r.data.tabs[0]?.id);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const settings = cfg?.settings;
  const encrypted = settings?.storage_mode === "encrypted";

  const loadTabRows = useCallback((tab) => {
    if (!encrypted) return; // ephemeral: nothing persisted
    api.get(`/ert/tabs/${tab}/rows`).then((r) => {
      setRowsByTab((p) => ({ ...p, [tab]: r.data.rows.map((x) => x.data) }));
    }).catch(() => {});
  }, [encrypted]);

  useEffect(() => { if (cfg && activeTab && encrypted && !rowsByTab[activeTab]) loadTabRows(activeTab); }, [cfg, activeTab, encrypted, rowsByTab, loadTabRows]);

  if (!cfg) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  if (!cfg.bcsi_authorized) return (
    <div className="max-w-2xl mx-auto p-10 text-center" data-testid="ert-locked">
      <LockKey size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
      <h1 className="text-xl font-bold text-foreground">BCSI Authorization Required</h1>
      <p className="text-sm text-muted-foreground mt-2">The CIP Evidence Request Tool contains BES Cyber System Information (BCSI). Ask an administrator to grant you BCSI access.</p>
    </div>
  );

  const isAdmin = me?.role === "admin";
  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/ert/settings", patch);
      setCfg((c) => ({ ...c, settings: data }));
      toast.success("Settings updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!settings.available) return (
    <div className="max-w-2xl mx-auto p-6" data-testid="ert-enable">
      <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck size={26} weight="duotone" /> CIP Evidence Request Tool</h1>
      <p className="text-sm text-muted-foreground mt-2">This optional module holds CIP audit evidence, including BCSI. It is required for Medium and High Impact entities.</p>
      {isAdmin ? (
        <div className="border border-border p-5 mt-5 space-y-4">
          <p className="text-sm">Choose how this entity will handle the data, then enable the module:</p>
          <StorageChoice value={settings.storage_mode} onChange={(m) => saveSettings({ storage_mode: m })} />
          <Button data-testid="ert-enable-btn" onClick={() => saveSettings({ enabled: true })}>Enable Module</Button>
        </div>
      ) : <p className="text-sm text-muted-foreground mt-4">This module is not enabled for your entity. Contact an administrator.</p>}
    </div>
  );

  const tab = cfg.tabs.find((t) => t.id === activeTab);
  const rows = rowsByTab[activeTab] || [];
  const setRows = (updater) => setRowsByTab((p) => ({ ...p, [activeTab]: typeof updater === "function" ? updater(p[activeTab] || []) : updater }));

  const setCell = (ri, key, val) => setRows((rs) => rs.map((r, i) => (i === ri ? { ...r, [key]: val } : r)));
  const addRow = () => setRows((rs) => [...rs, {}]);
  const delRow = (ri) => setRows((rs) => rs.filter((_, i) => i !== ri));
  const terminate = (ri) => setRows((rs) => rs.map((r, i) => (i === ri ? {
    ...r, termination_date: r.termination_date || TODAY(), access_changed: true,
    auth_electronic: false, auth_physical: false, auth_bcsi: false,
  } : r)));

  const saveTab = async () => {
    setBusy(true);
    try {
      await api.put(`/ert/tabs/${activeTab}/rows`, { rows: rows.map((data) => ({ data })) });
      toast.success("Saved & encrypted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const syncPersonnel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ert/personnel/sync");
      setRowsByTab((p) => { const n = { ...p }; delete n.personnel; return n; }); // force reload
      loadTabRows("personnel");
      toast.success(`Synced ${data.synced ?? 0} personnel record(s) from Access Management`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ert/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = {};
      Object.entries(data.tabs).forEach(([t, list]) => { next[t] = list.map((x) => x.data); });
      setRowsByTab(next);
      toast.success(`Imported: ${Object.entries(data.counts).map(([k, v]) => `${k} ${v}`).join(", ")}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onExport = async () => {
    setBusy(true);
    try {
      const body = encrypted ? {} : { tabs: Object.fromEntries(Object.entries(rowsByTab).map(([t, rs]) => [t, rs.map((data) => ({ data }))])) };
      const resp = await api.post("/ert/export", body, { responseType: "blob" });
      const href = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = href; a.download = "CIP_Evidence_Request_Tool.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(href);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-full p-6" data-testid="ert-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck size={24} weight="duotone" /> CIP Evidence Request Tool</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Storage: <span className="font-semibold">{encrypted ? "Encrypted (persisted)" : "Import/Export only (not stored)"}</span>
            {" · "}<span className="text-red-600 font-semibold">Contains BCSI</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-sm border border-border px-3 py-1.5 cursor-pointer hover:bg-secondary" data-testid="ert-import-label">
            <UploadSimple size={16} /> Import <input type="file" accept=".xlsx" className="hidden" onChange={onImport} data-testid="ert-import-input" />
          </label>
          <Button variant="outline" size="sm" className="gap-1" onClick={onExport} data-testid="ert-export-btn" disabled={busy}><DownloadSimple size={16} /> Export</Button>
          {encrypted && <Button size="sm" className="gap-1" onClick={saveTab} data-testid="ert-save-btn" disabled={busy}><FloppyDisk size={16} /> Save Tab</Button>}
        </div>
      </div>

      {isAdmin && (
        <div className="border border-border p-3 mb-4 flex items-center gap-4 flex-wrap" data-testid="ert-admin-bar">
          <span className="text-xs font-semibold uppercase text-muted-foreground">Admin</span>
          <StorageChoice value={settings.storage_mode} onChange={(m) => saveSettings({ storage_mode: m })} compact />
          <label className="text-sm flex items-center gap-1">
            <input type="checkbox" checked={!!settings.enabled} onChange={(e) => saveSettings({ enabled: e.target.checked })} data-testid="ert-enabled-toggle" /> Enabled
          </label>
        </div>
      )}

      <div className="flex gap-1 border-b border-border mb-3 flex-wrap" data-testid="ert-tabs">
        {cfg.tabs.map((t) => (
          <button key={t.id} data-testid={`ert-tab-${t.id}`} onClick={() => setActiveTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.name}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <Button variant="outline" size="sm" className="gap-1" onClick={addRow} data-testid="ert-addrow-btn"><Plus size={14} /> Add Row</Button>
        {activeTab === "personnel" && encrypted && isAdmin && (
          <Button variant="outline" size="sm" className="gap-1" onClick={syncPersonnel} data-testid="ert-personnel-sync-btn" disabled={busy}>
            <ArrowsClockwise size={14} /> Sync from Access Management
          </Button>
        )}
        <span className="text-xs text-muted-foreground">{rows.length} row(s)</span>
        {activeTab === "personnel" && encrypted && isAdmin && (
          <span className="text-[11px] text-muted-foreground">Populates identity from the CIP-004 People registry; set access flags manually.</span>
        )}
      </div>

      <div className="overflow-x-auto border border-border" data-testid="ert-grid">
        <table className="text-xs min-w-full">
          <thead className="bg-secondary">
            <tr>
              <th className="px-2 py-2 text-left font-semibold sticky left-0 bg-secondary">#</th>
              {tab.columns.map((c) => (
                <th key={c.key} className="px-2 py-2 text-left font-semibold whitespace-nowrap">
                  {c.label}{c.sensitive && <span className="ml-1 text-red-500" title="Sensitive / BCSI">•</span>}
                </th>
              ))}
              {activeTab === "personnel" && <th className="px-2 py-2">Action</th>}
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={tab.columns.length + 3} className="px-3 py-6 text-center text-muted-foreground" data-testid="ert-empty">No rows. Add a row or import a workbook.</td></tr>
            ) : rows.map((row, ri) => (
              <tr key={ri} className="border-t border-border" data-testid={`ert-row-${ri}`}>
                <td className="px-2 py-1 text-muted-foreground sticky left-0 bg-card">{ri + 1}</td>
                {tab.columns.map((c) => (
                  <td key={c.key} className="px-2 py-1">
                    <Cell col={c} value={row[c.key]} onChange={(v) => setCell(ri, c.key, v)} testid={`ert-cell-${ri}-${c.key}`} />
                  </td>
                ))}
                {activeTab === "personnel" && (
                  <td className="px-2 py-1">
                    <button onClick={() => terminate(ri)} className="text-orange-600 hover:text-orange-800 inline-flex items-center gap-1 text-xs" data-testid={`ert-terminate-${ri}`} title="Record termination & revoke access">
                      <UserMinus size={14} /> Term
                    </button>
                  </td>
                )}
                <td className="px-2 py-1">
                  <button onClick={() => delRow(ri)} className="text-muted-foreground hover:text-red-600" data-testid={`ert-delrow-${ri}`}><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cell({ col, value, onChange, testid }) {
  if (col.type === "bool") return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />;
  if (col.type === "select") return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="border border-border rounded px-1 py-1 text-xs min-w-[110px]">
      <option value=""></option>
      {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return <input type={col.type === "date" ? "date" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="border border-border rounded px-1 py-1 text-xs min-w-[130px]" />;
}

function StorageChoice({ value, onChange, compact }) {
  return (
    <div className={compact ? "flex items-center gap-3" : "space-y-2"}>
      {!compact && <p className="text-xs font-semibold uppercase text-muted-foreground">Data storage mode</p>}
      {[
        { v: "ephemeral", label: "Import/Export only — nothing stored" },
        { v: "encrypted", label: "Store with field-level encryption" },
      ].map((o) => (
        <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`ert-mode-${o.v}`}>
          <input type="radio" name="ert-mode" checked={value === o.v} onChange={() => onChange(o.v)} /> {o.label}
        </label>
      ))}
    </div>
  );
}
